import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

import { format } from 'date-fns';
import { throttle } from 'lodash';
import { first, Subject, takeUntil } from 'rxjs';

import { ColumnsDialogComponent } from 'app/components/shared/columns-dialog/columns-dialog.component';
import { COLUMNS, csvColumns, DEFAULT_OPERATIONS_COLUMNS } from 'app/constants/operations.constants';
import { DeviceMaxSize, StorageKeys } from 'app/constants/common.constants';
import { OperationsContentItem, OperationsTableColumn, OperationsColumn } from 'app/models/operations.models';
import { FilterService } from 'app/services/filter.service';
import { HeaderService } from 'app/services/header.service';
import { OperationsService } from 'app/services/operations.service';
import { getDialogConfig } from 'app/utils/columns-dialog.utils';

import { NgFor, NgIf } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatProgressBar } from '@angular/material/progress-bar';
import { RouterLink } from '@angular/router';
import { OperationsPaginatorIntl } from './services/operations-paginator-intl.service';

const getColumnValue = (value: unknown): string => {
  if (!value) return '-';
  return typeof value === 'string' ? value : value.toString();
};

const getDateValue = (value: unknown): string => {
  if (!value) return '-';
  return format(new Date(value as string), 'MMM dd yyyy, HH:mm');
};

@Component({
  selector: 'app-all-data-table',
  templateUrl: './operations.component.html',
  styleUrls: ['./operations.component.scss'],
  imports: [NgIf, MatProgressSpinner, MatProgressBar, MatTableModule, MatSortModule, NgFor, RouterLink, MatPaginator],
  providers: [{ provide: MatPaginatorIntl, useClass: OperationsPaginatorIntl }],
})
export class OperationsComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @ViewChild('paginator') paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  readonly columnsWithoutSort: OperationsColumn[] = [
    OperationsColumn.LastLoginDT,
    OperationsColumn.Rank,
    OperationsColumn.Badge,
  ];

  readonly tableColumns: OperationsTableColumn[] = [
    {
      columnDef: OperationsColumn.OfficerName,
      header: 'Officer Name',
      cell: ({ officerName }: OperationsContentItem) => getColumnValue(officerName),
    },
    {
      columnDef: OperationsColumn.Badge,
      header: 'Badge',
      cell: ({ officerNumber }: OperationsContentItem) => getColumnValue(officerNumber),
    },
    {
      columnDef: OperationsColumn.Rank,
      header: 'Rank',
      cell: ({ rank }: OperationsContentItem) => getColumnValue(rank),
    },
    {
      columnDef: OperationsColumn.LastLoginDT,
      header: 'Last Login DT',
      cell: ({ lastLoginDateTime }: OperationsContentItem) => getDateValue(lastLoginDateTime),
    },
    {
      columnDef: OperationsColumn.LastCitationDT,
      header: 'Last Citation DT',
      cell: ({ lastCitationDateTime }: OperationsContentItem) => getDateValue(lastCitationDateTime),
    },
    {
      columnDef: OperationsColumn.LastWarningDT,
      header: 'Last Warning DT',
      cell: ({ lastWarningDateTime }: OperationsContentItem) => getDateValue(lastWarningDateTime),
    },
    {
      columnDef: OperationsColumn.TotalCitations,
      header: 'Total Citations',
      cell: ({ totalCitationsCount }: OperationsContentItem) => getColumnValue(totalCitationsCount),
    },
    {
      columnDef: OperationsColumn.TotalWarnings,
      header: 'Total Warnings',
      cell: ({ totalWarningsCount }: OperationsContentItem) => getColumnValue(totalWarningsCount),
    },
    {
      columnDef: OperationsColumn.Last7DaysCitations,
      header: 'Last 7 Days Citations',
      cell: ({ last7DaysCitationsCount }: OperationsContentItem) => getColumnValue(last7DaysCitationsCount),
    },
    {
      columnDef: OperationsColumn.Last7DaysWarnings,
      header: 'Last 7 Days Warnings',
      cell: ({ last7DaysWarningsCount }: OperationsContentItem) => getColumnValue(last7DaysWarningsCount),
    },
    {
      columnDef: OperationsColumn.Last30DaysCitations,
      header: 'Last 30 Days Citations',
      cell: ({ last30DaysCitationsCount }: OperationsContentItem) => getColumnValue(last30DaysCitationsCount),
    },
    {
      columnDef: OperationsColumn.Last30DaysWarnings,
      header: 'Last 30 Days Warnings',
      cell: ({ last30DaysWarningsCount }: OperationsContentItem) => getColumnValue(last30DaysWarningsCount),
    },
  ];

  displayedColumns: OperationsColumn[] = DEFAULT_OPERATIONS_COLUMNS;
  dataSource = new MatTableDataSource<OperationsContentItem>([]);
  loading = false;

  private dialogRef?: MatDialogRef<ColumnsDialogComponent>;

  constructor(
    private operationsService: OperationsService,
    private selectColumnsDialog: MatDialog,
    private headerService: HeaderService,
    private filterService: FilterService,
  ) {
    const savedColumns = sessionStorage.getItem(StorageKeys.OPERATIONS_SELECTED_COLUMNS);
    this.displayedColumns = savedColumns ? JSON.parse(savedColumns) : DEFAULT_OPERATIONS_COLUMNS;
  }

  ngOnInit() {
    this.loadData();

    this.filterService.applyFiltersChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      this.loadData();
    });

    this.headerService.toggleColumnPanelChange.pipe(takeUntil(this.destroy$)).subscribe((isOpened: boolean) => {
      if (isOpened) {
        this.openEditColumnsPanel();
      }
    });

    this.headerService.exportChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const { filters } = this.filterService;

      this.operationsService
        .exportAll(
          filters,
          new HttpParams(),
          this.displayedColumns.map((el) => csvColumns[el]),
        )
        .subscribe((response) => {
          this.download('operations.csv', response);
        });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.sort.active = OperationsColumn.OfficerName;
    this.sort.direction = 'asc';
    this.dataSource.sort = this.sort;
  }

  loadData() {
    const { appliedFilters: filters } = this.filterService;
    this.loading = true;

    this.operationsService
      .getOperations(filters, new HttpParams())
      .pipe(first())
      .subscribe({
        next: (data: OperationsContentItem[]) => {
          this.dataSource.data = data;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  download(filename: string, text: string) {
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  openEditColumnsPanel() {
    this.dialogRef = this.selectColumnsDialog.open(
      ColumnsDialogComponent,
      getDialogConfig({
        columns: COLUMNS,
        defaultColumns: DEFAULT_OPERATIONS_COLUMNS,
        displayedColumns: this.displayedColumns,
      }),
    );

    this.dialogRef.afterClosed().subscribe((result) => {
      if (result != null) {
        sessionStorage.setItem(StorageKeys.OPERATIONS_SELECTED_COLUMNS, JSON.stringify(result));
      }
      this.headerService.toggleColumnPanel(false);
      if (result?.length) {
        this.updateEnabledColumns(result);
        this.loadData();
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize = throttle(() => {
    if (this.dialogRef && this.dialogRef.getState() === MatDialogState.OPEN) {
      this.dialogRef.updateSize('100%', window.innerWidth <= DeviceMaxSize.TABLET_HORIZONTAL ? '100%' : 'auto');
    }
  }, 100);

  updateEnabledColumns(newEnabledColumns: OperationsColumn[]) {
    this.displayedColumns = [...newEnabledColumns];
  }
}
